/**
 * Feature Flags Page
 *
 * Admin UI for the global flag registry. Changes apply to the backend
 * configured by VITE_API_URL.
 */

import { useState } from 'react';
import {
  AlertCircle,
  Pencil,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  FlagCreateDialog,
  FlagGatesPanel,
  FlagMetadataDialog,
  FlagRowIdentity,
  FlagRulesPanel,
  type FlagMetadataBody,
} from '@/components/feature-flags';
import {
  useArchiveFeatureFlag,
  useCreateFeatureFlag,
  useFeatureFlags,
  useUpdateFeatureFlag,
} from '@/hooks/use-feature-flags';
import { useToast } from '@/hooks/use-toast';
import { env } from '@/config/env';
import { requiresConfirmation } from '@/lib/api-env';
import {
  FLAG_CONFIRM_ON_ENABLE,
  getEnforcedFlagDisableCopy,
  PLATFORM_TAG_COPY,
} from '@/types/feature-flag';
import type { CreateFeatureFlagBody, FeatureFlagDefinition } from '@/types/feature-flag';

interface PendingToggle {
  definition: FeatureFlagDefinition;
  nextValue: boolean;
}

export default function FeatureFlagsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useFeatureFlags();
  const updateMutation = useUpdateFeatureFlag();
  const createMutation = useCreateFeatureFlag();
  const archiveMutation = useArchiveFeatureFlag();
  const { toast } = useToast();

  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);
  const [pendingArchive, setPendingArchive] = useState<FeatureFlagDefinition | null>(null);
  const [pendingGatesFor, setPendingGatesFor] = useState<FeatureFlagDefinition | null>(null);
  const [pendingRulesFor, setPendingRulesFor] = useState<FeatureFlagDefinition | null>(null);
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingMetadataFor, setPendingMetadataFor] = useState<FeatureFlagDefinition | null>(null);
  // Its own error, not updateMutation.error: the toggle shares that mutation,
  // so a failed toggle on one flag would otherwise surface in another's editor.
  const [metadataError, setMetadataError] = useState<Error | null>(null);

  const prodGuard = requiresConfirmation(env.VITE_API_URL);

  // A set, not a single key: two flags can be in flight at once, and one
  // settling must not unlock the other's switch mid-request.
  const markPending = (key: string) => setPendingKeys(prev => new Set(prev).add(key));
  const clearPending = (key: string) =>
    setPendingKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const requestToggle = (definition: FeatureFlagDefinition, nextValue: boolean) => {
    // Two independent guards, never both live on today's data: enabling a
    // flag in FLAG_CONFIRM_ON_ENABLE (e.g. the kill switch — advisory, but
    // severe) and disabling an *enforced* flag (blocks traffic the instant
    // it flips).
    const needsEnableConfirm = nextValue && FLAG_CONFIRM_ON_ENABLE[definition.key] !== undefined;
    const needsDisableConfirm = !nextValue && definition.enforced;
    if (needsEnableConfirm || needsDisableConfirm || prodGuard) {
      setPendingToggle({ definition, nextValue });
      return;
    }
    applyToggle(definition, nextValue);
  };

  const applyToggle = (definition: FeatureFlagDefinition, nextValue: boolean) => {
    markPending(definition.key);
    updateMutation.mutate(
      { key: definition.key, body: { enabled: nextValue } },
      {
        onSuccess: () =>
          toast({
            title: 'Feature flag updated',
            description: `${definition.label} is now ${nextValue ? 'ON' : 'OFF'}.`,
          }),
        onError: err =>
          toast({
            title: 'Update failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
        onSettled: () => clearPending(definition.key),
      }
    );
  };

  const openMetadata = (definition: FeatureFlagDefinition) => {
    setMetadataError(null);
    setPendingMetadataFor(definition);
  };

  const handleMetadataSave = (body: FlagMetadataBody) => {
    if (!pendingMetadataFor) return;
    const definition = pendingMetadataFor;
    setMetadataError(null);
    markPending(definition.key);
    updateMutation.mutate(
      { key: definition.key, body },
      {
        onSuccess: () => {
          setPendingMetadataFor(null);
          toast({
            title: 'Flag details updated',
            description: `${body.label} now reads as ${PLATFORM_TAG_COPY[body.platformTag].label}.`,
          });
        },
        onError: err => setMetadataError(err instanceof Error ? err : new Error('Unknown error')),
        onSettled: () => clearPending(definition.key),
      }
    );
  };

  const handleCreate = (body: CreateFeatureFlagBody) => {
    createMutation.mutate(
      { scope: 'global', body },
      {
        onSuccess: definition => {
          toast({
            title: 'Feature flag created',
            description: `${definition.label} is available to target. Clients ignore it until they ship code that reads it.`,
          });
          setCreateOpen(false);
        },
        onError: err =>
          toast({
            title: 'Could not create the flag',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
      }
    );
  };

  const handleArchive = () => {
    if (!pendingArchive) return;
    archiveMutation.mutate(
      { scope: 'global', key: pendingArchive.key },
      {
        onSuccess: () => {
          toast({ title: 'Feature flag archived' });
          setPendingArchive(null);
        },
        onError: err =>
          toast({
            title: 'Could not archive the flag',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
      }
    );
  };

  const confirmCopy = pendingToggle
    ? pendingToggle.nextValue
      ? FLAG_CONFIRM_ON_ENABLE[pendingToggle.definition.key]
      : pendingToggle.definition.enforced
        ? {
            title: `Turn off ${pendingToggle.definition.label}?`,
            description: `This flag is enforced by the backend — it ${getEnforcedFlagDisableCopy(pendingToggle.definition.key)}`,
          }
        : undefined
    : undefined;

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Environment</AlertTitle>
        <AlertDescription>
          You are editing flags on <code className="font-mono">{env.VITE_API_URL}</code>.
          Double-check before changing anything that affects production.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Feature Flags</CardTitle>
            <CardDescription>
              Global flags read by mobile + web clients via{' '}
              <code className="font-mono">/v1/app/config</code>. &quot;Blocks traffic&quot; flags
              are read by the backend directly and reject platform/version overrides.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh feature flags"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Create flag
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Failed to load flags</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {data && data.definitions.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No feature flags yet. Create one to get started.
            </p>
          )}

          {data && data.definitions.length > 0 && (
            <div className="divide-y rounded-md border">
              {data.definitions.map(definition => {
                const enabled = data.flags[definition.key] ?? definition.defaultEnabled;

                return (
                  <div key={definition.key}>
                    <div className="flex items-start justify-between gap-4 p-4">
                      <FlagRowIdentity definition={definition} />

                      <div className="flex shrink-0 items-center gap-2">
                        <Switch
                          checked={enabled}
                          onCheckedChange={next => requestToggle(definition, next)}
                          disabled={pendingKeys.has(definition.key)}
                          aria-label={`Toggle ${definition.label}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => openMetadata(definition)}
                          aria-label={`Edit details for ${definition.label}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setPendingRulesFor(definition)}
                          aria-label={`Identity targeting for ${definition.label}`}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => setPendingGatesFor(definition)}
                          aria-label={`Platform and version overrides for ${definition.label}`}
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                        </Button>
                        {/* Built-ins can be gated (see the icon above) but not
                            archived — their defaultEnabled is code-defined. */}
                        {!definition.isBuiltIn && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive"
                            onClick={() => setPendingArchive(definition)}
                            aria-label={`Archive ${definition.label}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <FlagCreateDialog
        open={createOpen}
        scopeLabel="global"
        isSubmitting={createMutation.isPending}
        onSubmit={handleCreate}
        onClose={() => setCreateOpen(false)}
      />

      <FlagMetadataDialog
        definition={pendingMetadataFor}
        prodGuard={prodGuard}
        busy={pendingMetadataFor !== null && pendingKeys.has(pendingMetadataFor.key)}
        error={metadataError}
        onSubmit={handleMetadataSave}
        onClose={() => setPendingMetadataFor(null)}
      />

      <FlagGatesPanel
        scope="global"
        definition={pendingGatesFor}
        onClose={() => setPendingGatesFor(null)}
      />

      <FlagRulesPanel
        scope="global"
        definition={pendingRulesFor}
        onClose={() => setPendingRulesFor(null)}
      />

      <AlertDialog
        open={pendingToggle !== null}
        onOpenChange={open => {
          if (!open) setPendingToggle(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy ? confirmCopy.title : 'Confirm change'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmCopy ? (
                <>
                  {confirmCopy.description}
                  {prodGuard && (
                    <span className="mt-2 block">
                      You are making this change against{' '}
                      <code className="font-mono">{env.VITE_API_URL}</code>. This URL is not
                      recognized as a non-prod environment, so the change may affect live users
                      immediately.
                    </span>
                  )}
                </>
              ) : (
                <>
                  You are about to set <strong>{pendingToggle?.definition.label ?? ''}</strong> to{' '}
                  <strong>{pendingToggle?.nextValue ? 'ON' : 'OFF'}</strong> against{' '}
                  <code className="font-mono">{env.VITE_API_URL}</code>. This URL is not recognized
                  as a non-prod environment, so the change may affect live users immediately.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingToggle) {
                  applyToggle(pendingToggle.definition, pendingToggle.nextValue);
                  setPendingToggle(null);
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingArchive !== null}
        onOpenChange={open => {
          // Same guard as the create/gate dialogs: Escape must not dismiss a
          // confirmation whose request is still in flight.
          if (!open && !archiveMutation.isPending) setPendingArchive(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {pendingArchive?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              The flag stops being served and disappears from this list. Its stored value is
              cleared, but the definition and audit history are kept — creating the same key again
              revives it.
              {prodGuard && (
                <span className="mt-2 block">
                  You are archiving against <code className="font-mono">{env.VITE_API_URL}</code>.
                  This URL is not recognized as a non-prod environment, so clients stop receiving
                  this flag immediately.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={event => {
                event.preventDefault();
                handleArchive();
              }}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
